/**
 * Hook to register the Loyalty tab with DetailPageTabsContext.
 *
 * Adds a "Loyalty" tab to the Customer detail page only. Consolidates the
 * customer's points/rewards (from the /rewards module) into their profile:
 * points balance, tier, point history, claimed rewards, and adjust-points.
 *
 * Mirrors useItemPriceTab exactly (functional setTabs updater, no `tabs`
 * dependency, self-removing, inserts after the Dashboard tab). See that file
 * for the guardrail notes — do NOT add a watchdog effect.
 */

import { useEffect } from 'react';
import { useDetailPageTabs } from '@/contexts/DetailPageTabsContext';
import type { TabStructure } from '@/features/inventory/item-detail/utils/buildTabsFromMeta';
import { useLanguage } from '@/i18n/useLanguage';
import { getTabLabelTranslation } from '@/lib/views/labels/translations';
import { DASHBOARD_TAB_ID } from '@/features/common/doc-dashboard/useDashboardTab';

/** Tab ID constant — reference this everywhere the Loyalty tab is used. */
export const LOYALTY_TAB_ID = 'loyalty';

interface UseLoyaltyTabOptions {
  doctype: string;
  name?: string;
  enabled?: boolean;
}

export function useLoyaltyTab({ doctype, name, enabled = true }: UseLoyaltyTabOptions) {
  const { setTabs } = useDetailPageTabs();
  const { language } = useLanguage();
  const loyaltyLabel = getTabLabelTranslation('Loyalty', language) || 'Loyalty';

  useEffect(() => {
    const isCustomerDoctype = doctype === 'Customer';
    const shouldShow = enabled && isCustomerDoctype && !!name && name !== 'new';

    setTabs((prevTabs: TabStructure[]) => {
      const exists = prevTabs.some((t) => t.name === LOYALTY_TAB_ID);

      const loyaltyTab: TabStructure = {
        name: LOYALTY_TAB_ID,
        label: loyaltyLabel,
        sections: [], // Empty — content is rendered separately in ExperienceView
      };

      if (shouldShow) {
        if (!exists) {
          const dashboardIdx = prevTabs.findIndex((t) => t.name === DASHBOARD_TAB_ID);
          if (dashboardIdx !== -1) {
            const newTabs = [...prevTabs];
            newTabs.splice(dashboardIdx + 1, 0, loyaltyTab);
            return newTabs;
          }
          return [...prevTabs, loyaltyTab];
        }

        // Keep label in sync (language switch)
        const currentIdx = prevTabs.findIndex((t) => t.name === LOYALTY_TAB_ID);
        if (currentIdx !== -1 && prevTabs[currentIdx].label !== loyaltyLabel) {
          const newTabs = [...prevTabs];
          newTabs[currentIdx] = { ...prevTabs[currentIdx], label: loyaltyLabel };
          return newTabs;
        }
        return prevTabs;
      }

      if (!shouldShow && exists) {
        return prevTabs.filter((t) => t.name !== LOYALTY_TAB_ID);
      }

      return prevTabs;
    });
  }, [doctype, name, enabled, setTabs, loyaltyLabel]);

  return {
    tabId: LOYALTY_TAB_ID,
    isEnabled: enabled && doctype === 'Customer' && !!name && name !== 'new',
  };
}
